import { MarketCapOptions } from '../../constants/statsOptions'

const MarketCapIds = MarketCapOptions.map(opt => opt.id)

interface MarketCapRange {
  id: string
  value: string
  min: number
  max: number
}

/**
 * Parse min/max range from a market cap display value string
 * Handles formats like "< £100 million", "£100 million < £500 million", "> £50 billion"
 */
function parseRangeFromValue (value: string): { min: number, max: number } {
  const lowerValue: string = value.toLowerCase()

  if (lowerValue === 'not applicable') {
    return { min: -Infinity, max: -Infinity }
  }

  // Convert "million" to 1e6 and "billion" to 1e9
  const extractNumber = (str: string): number => {
    const numMatch: RegExpMatchArray | null = str.match(/[\d.]+/)
    if (!numMatch) {
      return 0
    }
    const num: number = parseFloat(numMatch[0])
    if (str.includes('billion')) {
      return num * 1e9
    }
    if (str.includes('million')) {
      return num * 1e6
    }
    return num
  }

  // Handle "< £X" format (upper bound only)
  if (lowerValue.startsWith('<')) {
    const max: number = extractNumber(lowerValue)
    return { min: 0, max: max - 1 }
  }

  // Handle "> £X" format (lower bound only)
  if (lowerValue.startsWith('>')) {
    const min: number = extractNumber(lowerValue)
    return { min: min + 1, max: Infinity }
  }

  // Handle "£X < £Y" format (range)
  const parts: string[] = lowerValue.split('<')
  if (parts.length === 2) {
    return {
      min: extractNumber(parts[0]),
      max: extractNumber(parts[1])
    }
  }

  return { min: 0, max: Infinity }
}

/**
 * Get market cap options with dynamically parsed ranges
 */
function getMarketCapRanges (): MarketCapRange[] {
  return MarketCapOptions.map(opt => ({
    ...opt,
    ...parseRangeFromValue(opt.value)
  }))
}

/**
 * Extract a number from various market cap string formats
 * Handles: "500m", "1.5b", "£2 billion", "100 million", "1,000,000,000", etc.
 */
function extractMarketCapValue (value: string): number | undefined {
  const lowerValue: string = value.toLowerCase().replace(/[£$€,\s]/g, '')

  // Check for billion indicators
  const billionMatch: RegExpMatchArray | null = lowerValue.match(/([\d.]+)\s*(?:b|bn|billion)/)
  if (billionMatch) {
    return parseFloat(billionMatch[1]) * 1e9
  }

  // Check for million indicators
  const millionMatch: RegExpMatchArray | null = lowerValue.match(/([\d.]+)\s*(?:m|mn|million)/)
  if (millionMatch) {
    return parseFloat(millionMatch[1]) * 1e6
  }

  // Check for plain number (assume it's in raw form)
  const plainMatch: RegExpMatchArray | null = lowerValue.match(/^[\d.]+$/)
  if (plainMatch) {
    return parseFloat(plainMatch[0])
  }

  return undefined
}

/**
 * Find the matching range for a given market cap value
 */
function findRangeForValue (num: number): string | undefined {
  const ranges: MarketCapRange[] = getMarketCapRanges()
  const matchingRange: MarketCapRange | undefined = ranges.find(range =>
    range.min !== -Infinity && num >= range.min && num <= range.max
  )
  return matchingRange?.id
}

/**
 * Normalize a market cap input to the appropriate range ID
 * Handles various formats:
 * - Numbers: 500000000, 1000000000
 * - Strings with units: "500m", "1.5b", "£2 billion", "100 million"
 * - Already valid IDs: "Sub100m", "1bTo5b", "NotApplicable"
 * - Unparseable or non-numeric text (e.g. "N/A", "private") maps to NotApplicable
 *
 * @param input - Raw market cap value
 * @returns A matching range ID; `'NotApplicable'` when the value is present but
 *   unparseable or falls outside known bands; `undefined` when input is missing,
 *   empty, or a negative number
 */
export default function normalizeMarketCap (input: string | number | undefined): string | undefined {
  if (input === undefined || input === null || input === '') {
    return undefined
  }

  if (typeof input === 'number') {
    if (input < 0) {
      return undefined
    }
    return findRangeForValue(input) ?? 'NotApplicable'
  }

  const stringValue: string = String(input).trim()

  if (MarketCapIds.includes(stringValue)) {
    return stringValue
  }

  // Try to extract a numeric value
  const numericValue: number | undefined = extractMarketCapValue(stringValue)
  if (numericValue === undefined) {
    return 'NotApplicable'
  }

  return findRangeForValue(numericValue) ?? 'NotApplicable'
}
